import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { isNil, head } from 'ramda'
import {
  isFalsy,
  isUndefined,
  isEmptyString,
  isEmptyArray
} from 'ramda-adjunct'
import { useIntl } from 'gatsby-plugin-intl'
import { breakpoint } from 'src/theme/grid'

import useClickOutside from 'src/hooks/useClickOutside'
import {
  FilterBox,
  FilterDropdown,
  SearchInput,
  SearchInputWrapper,
  SelectOptionsWrapper,
  SelectRow,
  OptionsSectionTitle,
  Separator
} from './components/atoms'

const SelectBox = ({
  className,
  id,
  name,
  value,
  label,
  options,
  onChange,
  onConfirm,
  onDropdownClose,
  placeholder,
  alignRight,
  dropdownBoundaryInset = 8,
  dropdownBoundaryRef,
  dropdownMinHeight = 0,
  dropdownTriggerGap = 4,
  fitDropdownToBoundary = false,
  removeSearchInput = false,
  removeSelectedOptions = false,
  stickyActions = false,
  type = 'checkbox'
}) => {
  const intl = useIntl()
  const [filterOpened, setFilterOpened] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [selectedOptions, setSelectedOptions] = useState([])
  const [boundaryDropdownStyle, setBoundaryDropdownStyle] = useState(null)
  const wasDropdownOpened = useRef(false)
  const closeDropdown = () => {
    setFilterOpened(false)
    setBoundaryDropdownStyle(null)
  }
  const toggleDropdown = () => {
    if (filterOpened) {
      closeDropdown()
      return
    }
    setBoundaryDropdownStyle(null)
    setFilterOpened(true)
  }
  const componentRef = useClickOutside(closeDropdown)
  const emptyText = isEmptyString(searchText) || searchText.length < 2

  useEffect(() => {
    if (wasDropdownOpened.current && !filterOpened && onDropdownClose) {
      onDropdownClose()
    }
    wasDropdownOpened.current = filterOpened
  }, [filterOpened, onDropdownClose])

  useEffect(() => {
    if (filterOpened) {
      setSelectedOptions(parseValueToOptions())
    }
  }, [filterOpened])

  useEffect(() => {
    if (!filterOpened || !fitDropdownToBoundary || !dropdownBoundaryRef) {
      return undefined
    }

    const boundary = dropdownBoundaryRef.current
    const trigger =
      componentRef.current && componentRef.current.firstElementChild
    if (!boundary || !trigger) return undefined

    const updateDropdownLayout = () => {
      const boundaryRect = boundary.getBoundingClientRect()
      const triggerRect = trigger.getBoundingClientRect()
      const inset = Math.max(0, Number(dropdownBoundaryInset) || 0)
      const triggerGap = Math.max(0, Number(dropdownTriggerGap) || 0)
      const availableWidth = Math.max(0, boundaryRect.width - inset * 2)
      const width = Math.min(triggerRect.width + 1, availableWidth)
      const left = Math.min(
        Math.max(triggerRect.left, boundaryRect.left + inset),
        boundaryRect.right - inset - width
      )
      const bottom = boundaryRect.bottom - inset
      const boundaryHeader = boundary.firstElementChild
      const headerBottom = boundaryHeader
        ? boundaryHeader.getBoundingClientRect().bottom
        : boundaryRect.top
      const minimumTop = Math.max(boundaryRect.top + inset, headerBottom)
      const preferredTop = Math.min(
        Math.max(triggerRect.bottom + triggerGap, minimumTop),
        bottom
      )
      const minimumHeight = Math.max(0, Number(dropdownMinHeight) || 0)
      const height = Math.min(
        Math.max(0, bottom - minimumTop),
        Math.max(bottom - preferredTop, minimumHeight)
      )
      const top = Math.max(minimumTop, Math.min(preferredTop, bottom - height))

      setBoundaryDropdownStyle({
        position: 'fixed',
        top: Math.round(top),
        left: Math.round(left),
        width: Math.round(width),
        height: Math.max(0, Math.round(height))
      })
    }

    updateDropdownLayout()
    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(updateDropdownLayout)
    if (resizeObserver) {
      resizeObserver.observe(boundary)
      resizeObserver.observe(trigger)
    }
    window.addEventListener('resize', updateDropdownLayout)
    window.addEventListener('scroll', updateDropdownLayout, true)

    return () => {
      if (resizeObserver) resizeObserver.disconnect()
      window.removeEventListener('resize', updateDropdownLayout)
      window.removeEventListener('scroll', updateDropdownLayout, true)
    }
  }, [
    dropdownBoundaryInset,
    dropdownBoundaryRef,
    dropdownMinHeight,
    dropdownTriggerGap,
    filterOpened,
    fitDropdownToBoundary
  ])

  const isMultiple = () => Array.isArray(value)

  const generateFilterName = () => {
    const selectedValue = isMultiple() ? head(value) : value
    if (isNil(selectedValue) || isUndefined(selectedValue)) {
      return label
    }

    const selectedOption = head(
      options.filter(opt => opt.value === selectedValue)
    )
    return !isFalsy(selectedOption) ? selectedOption.label : label
  }

  const handleChange = (optionValue, isThisValueSelected) => {
    const singleValue = () => (isThisValueSelected ? null : optionValue)
    const multipleValue = () =>
      isThisValueSelected
        ? value.filter(v => v !== optionValue)
        : [...value, optionValue]
    onChange(name, isMultiple() ? multipleValue() : singleValue())
  }

  const handleClear = e => {
    !isFalsy(e) && e.stopPropagation()
    setSearchText('')
    onChange(name, isMultiple() ? [] : null)
  }

  const handleConfirm = () => {
    closeDropdown()
    if (onConfirm) onConfirm()
  }

  const filteredOptions = () =>
    searchText === ''
      ? options
      : options.filter(o =>
          o.label.toLowerCase().includes(searchText.toLowerCase())
        )

  const isSelected = optionValue => {
    const singleSelected = () => value === optionValue
    const multipleSelected = () => value.includes(optionValue)

    return isMultiple() ? multipleSelected() : singleSelected()
  }

  const parseValueToOptions = () => {
    const values = !isMultiple() ? [value] : value
    return values.map(v => head(options.filter(option => option.value === v)))
  }

  return (
    <div className={className} ref={componentRef}>
      <FilterBoxStyled
        onClick={toggleDropdown}
        opened={filterOpened}
        hasValue={isMultiple() ? value.length > 0 : !isNil(value)}
        id={`filter-${id}`}
        selectedNumber={isMultiple() ? value.length : null}
        onClear={handleClear}
      >
        {generateFilterName()}
      </FilterBoxStyled>
      {filterOpened && (
        <FilterDropdown
          alignRight={alignRight}
          filterName={label}
          stickyActions={stickyActions}
          style={
            fitDropdownToBoundary
              ? boundaryDropdownStyle || { visibility: 'hidden' }
              : undefined
          }
          onClose={closeDropdown}
          onConfirm={handleConfirm}
          onClear={handleClear}
          id={`select-${id}`}
          clearButtonText={intl.formatMessage({ id: 'offersFilters.clear' })}
          confirmButtonText={intl.formatMessage({
            id: 'offersFilters.confirm'
          })}
        >
          {!removeSearchInput && (
            <SearchInputWrapper>
              <SearchInput
                name={name}
                id={`input-${id}`}
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                placeholder={placeholder}
              />
            </SearchInputWrapper>
          )}
          <SelectOptionsWrapper>
            {!removeSelectedOptions &&
              emptyText &&
              !isEmptyArray(selectedOptions) && (
                <>
                  <OptionsSectionTitle>
                    {intl.formatMessage({ id: 'offersFilters.chosenOptions' })}
                  </OptionsSectionTitle>
                  {selectedOptions.map((option, index) => {
                    const selected = isSelected(option.value)
                    return (
                      <SelectRow
                        key={index}
                        type={type}
                        selected={selected}
                        name={option.label}
                        onClick={() => handleChange(option.value, selected)}
                      />
                    )
                  })}
                  <Separator />
                </>
              )}

            {filteredOptions().map((option, index) => {
              const selected = isSelected(option.value)
              return (
                <SelectRow
                  key={index}
                  type={type}
                  selected={selected}
                  name={option.label}
                  onClick={() => handleChange(option.value, selected)}
                />
              )
            })}
          </SelectOptionsWrapper>
        </FilterDropdown>
      )}
    </div>
  )
}

SelectBox.defaultProps = {
  id: '',
  label: '',
  name: '',
  value: '',
  placeholder: '',
  options: [],
  onChange: () => {}
}

export default styled(SelectBox)`
  display: flex;
  position: relative;
  ${FilterDropdown} {
    ${breakpoint.m`
      width: ${({ width }) => width || '220px'};
      height: ${({ height }) => height || '400px'};
    `}
  }
`

const FilterBoxStyled = styled(FilterBox)`
  width: 100%;
  ${breakpoint.m`
    width: 180px;
  `}
`
